// Maps Bronnum product/basket data to the Klaviyo event payloads defined in the customer's
// "Data & Metrics" sheet. Pure mappers (no side effects) — fire them via this.$repositories.klaviyo.track(...).

const last2 = (sku) => (sku && sku.length >= 2 ? sku.slice(-2) : '')

// Last-resort fallback only. Guesses the packshot URL from the SKU with a hardcoded ".jpg",
// which 404s for products whose real asset is ".png". Prefer the real image URL the app has
// already resolved (product.images / basket store images) via the helpers below.
export const productImageUrl = (sku) =>
  sku ? `${process.env.kontainerUrl}/bronnum/cache/pack/${last2(sku)}/${sku}.jpg` : null

// The app's resolved packshots (product.images / basket store images) embed the real
// "/bronnum/cache/pack/..." path with the correct extension. Turn it into an absolute CDN URL
// for Klaviyo emails, the same ${kontainerUrl}${path} way the rest of the app builds image URLs.
const toCdnUrl = (url) => {
  const path = url && String(url).match(/\/bronnum\/.*$/)?.[0]
  return path ? `${process.env.kontainerUrl}${path}` : (url || null)
}

const objectImageUrl = (obj) => toCdnUrl(obj?.images?.[0]?.url ?? obj?.images?.[0])
const skuImageUrl = (sku, images) => toCdnUrl(images?.find((i) => i?.sku === sku)?.url)

// Best available image for a line: basket store images → line's own images → SKU-guess fallback.
const lineImageUrl = (line, images) =>
  skuImageUrl(line?.sku, images) || objectImageUrl(line) || productImageUrl(line?.sku)

const basketStoreImages = (component) => component?.$store?.getters['cart/basket/getImages'] || null

const getProp = (product, name) =>
  product?.properties?.find((p) => p.name?.toLowerCase() === name.toLowerCase() && p.value !== '')?.value ?? null

const currentUrl = () => (typeof window !== 'undefined' ? window.location.href : null)

const absoluteUrl = (url) => {
  if (!url || typeof window === 'undefined') {
    return url ?? null
  }

  return new URL(url, window.location.origin).href
}

const priceExVat = (price) => price?.priceExVat ?? price?.priceAmount ?? price?.price ?? null
const priceInclVat = (price) => price?.price ?? price?.priceInclVat ?? null
const regularPriceExVat = (price) => {
  const regular = price?.regularExVat ?? price?.regular ?? null
  const current = priceExVat(price)

  return regular != null && current != null && regular > current ? regular : null
}
const regularPriceInclVat = (price) => {
  const regular = price?.regular ?? price?.regularInclVat ?? null
  const current = priceInclVat(price)

  return regular != null && current != null && regular > current ? regular : null
}
const basketTotalExVat = (summary) => summary?.totalPriceExVat ?? summary?.totalPriceExShippingExVat ?? (summary?.totalPrice != null && summary?.totalVat != null ? summary.totalPrice - summary.totalVat : null)
const basketTotalInclVat = (summary) => summary?.totalPrice ?? (summary?.totalPriceExVat != null && summary?.totalVat != null ? summary.totalPriceExVat + summary.totalVat : null)

const categoryNames = (product, categoryPath = null) => {
  const path = categoryPath ?? getProp(product, 'categoryPath') ?? getProp(product, 'categories') ?? getProp(product, 'category') ?? product?.categoryPath

  if (Array.isArray(path)) {
    return path.filter(Boolean)
  }

  if (path) {
    return path.split(/[>/]/).map((c) => c.trim()).filter(Boolean)
  }

  return product?.primaryCategoryName ? [product.primaryCategoryName] : []
}

const categoryPayload = (product, categoryPath = null) => {
  const categories = categoryNames(product, categoryPath)

  return {
    Categories: categories,
    Category: categories[categories.length - 1] ?? product?.primaryCategoryName ?? null,
    CategoryPath: categories.join(' > ') || product?.primaryCategoryName || null,
    CategorySlugPath: categories.length ? `/${categories.map((c) => c.toLowerCase().replace(/\s+/g, '-')).join('/')}` : null
  }
}

const basketLines = (basket) => (basket?.orderLines ?? []).filter((l) => !(l?.customProperties && (l.customProperties.Addon || l.customProperties.addon)))

const basketItemPayload = (line, images = null) => ({
  ProductID: line?.sku,
  Name: line?.name ?? line?.title,
  Quantity: line?.quantity,
  Price: priceExVat(line?.price),
  PriceInclVat: priceInclVat(line?.price),
  RowTotal: priceExVat(line?.price) && line?.quantity ? priceExVat(line?.price) * line.quantity : null,
  ImageURL: lineImageUrl(line, images),
  URL: absoluteUrl(line?.variant?.url ?? line?.productSlug)
})

// CSV "Viewed Product" — single product.
export const mapViewedProduct = (product, categoryPath = null) => ({
  Brand: getProp(product, 'brand'),
  ...categoryPayload(product, categoryPath),
  CompareAtPrice: regularPriceExVat(product?.price),
  CompareAtPriceInclVat: regularPriceInclVat(product?.price),
  ImageURL: objectImageUrl(product) || productImageUrl(product?.sku),
  Name: product?.title ?? product?.name ?? null,
  Price: priceExVat(product?.price),
  PriceInclVat: priceInclVat(product?.price),
  ProductID: product?.sku ?? null,
  URL: currentUrl()
})

// CSV "Viewed Collection" — category page (custom; no Klaviyo guide). Keep it simple + useful.
export const mapViewedCollection = (categoryName, products = []) => ({
  CollectionName: categoryName ?? null,
  ItemNames: products.map((p) => getProp(p, 'description1') ?? p?.title ?? p?.name ?? p?.displayName ?? p?.productName).filter(Boolean),
  ProductIDs: products.map((p) => p?.sku).filter(Boolean),
  URL: currentUrl()
})

// CSV "Checkout Started" — multi-item + a unique checkout URL.
export const mapCheckoutStarted = (basket, checkoutUrl = null, images = null) => {
  const lines = basketLines(basket)
  const items = lines.map((line) => basketItemPayload(line, images))

  return {
    ItemNames: items.map((l) => l?.Name).filter(Boolean),
    ProductIDs: items.map((l) => l?.ProductID).filter(Boolean),
    Items: items,
    BasketTotal: basketTotalExVat(basket?.orderSummary),
    BasketTotalInclVat: basketTotalInclVat(basket?.orderSummary),
    BasketURL: currentUrl(),
    CheckoutURL: checkoutUrl ? absoluteUrl(checkoutUrl) : currentUrl()
  }
}

export const basketTrackingId = (basket) => basket?.id || basket?.basketId || basket?.orderNumber || basketLines(basket).map((line) => `${line.sku}:${line.quantity}`).join('|')

const orderTrackingId = (orderGuid, order = null) => order?.orderNumber || orderGuid

export const mapPaymentReturn = (orderGuid, status, order = null, images = null) => {
  const items = basketLines(order).map((line) => basketItemPayload(line, images))

  return {
    OrderGuid: orderGuid,
    OrderNumber: order?.orderNumber ?? null,
    PaymentStatus: status,
    URL: currentUrl(),
    BasketTotal: basketTotalExVat(order?.orderSummary),
    BasketTotalInclVat: basketTotalInclVat(order?.orderSummary),
    ItemNames: items.map((l) => l?.Name).filter(Boolean),
    ProductIDs: items.map((l) => l?.ProductID).filter(Boolean),
    Items: items
  }
}

export const trackPaymentReturnOnce = (component, eventName, orderGuid, status, order = null, email = null) => {
  if (!process.client || !orderGuid) {
    return
  }

  const trackingId = orderTrackingId(orderGuid, order)
  const storageKey = `klaviyoPaymentReturn:${eventName}:${trackingId}`

  if (sessionStorage.getItem(storageKey)) {
    return
  }

  sessionStorage.setItem(storageKey, 'pending')
  return component.$repositories.klaviyo.track(
    eventName,
    mapPaymentReturn(orderGuid, status, order, basketStoreImages(component)),
    email || component.$store.getters['user/user/getUser']?.email || order?.billingAddress?.email || null,
    trackingId
  ).then(() => {
    sessionStorage.setItem(storageKey, 'true')
  }).catch(() => {
    sessionStorage.removeItem(storageKey)
  })
}

export const trackCheckoutStartedOnce = (component, basket, checkoutUrl = null, email = null) => {
  if (!process.client) {
    console.debug('[Klaviyo] Checkout Started skipped: not client')
    return
  }

  if (!basket?.orderLines?.length) {
    console.debug('[Klaviyo] Checkout Started skipped: empty basket', { basket })
    return
  }

  const resolvedEmail = email || component.$store.getters['user/user/getUser']?.email || component.$store.getters['cart/basket/getBasket']?.billingAddress?.email || null
  if (!resolvedEmail) {
    console.debug('[Klaviyo] Checkout Started skipped: missing email')
    return
  }

  const basketId = basketTrackingId(basket)
  const storageKey = `klaviyoCheckoutStarted:${basketId}`
  const existingValue = sessionStorage.getItem(storageKey)

  if (existingValue) {
    console.debug('[Klaviyo] Checkout Started skipped: session key exists', { storageKey, existingValue })
    return
  }

  const payload = mapCheckoutStarted(basket, checkoutUrl, basketStoreImages(component))
  console.debug('[Klaviyo] Checkout Started sending', { storageKey, basketId, email: resolvedEmail, payload })

  sessionStorage.setItem(storageKey, 'pending')
  return component.$repositories.klaviyo.track(
    'Checkout Started',
    payload,
    resolvedEmail,
    basketId
  ).then(() => {
    console.debug('[Klaviyo] Checkout Started sent', { storageKey })
    sessionStorage.setItem(storageKey, 'true')
  }).catch((error) => {
    console.debug('[Klaviyo] Checkout Started failed', { storageKey, error })
    sessionStorage.removeItem(storageKey)
  })
}
